import { Bell, BellOff, Send, Smartphone, Trash2 } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { getWebPushDevices, postWebPushUnsubscribe } from "@client/api/endpoints";
import { useAsync } from "@client/hooks/useAsync";
import { useNotification } from "@client/hooks/useNotification";
import { getSubscription } from "@client/lib/push";
import { t } from "@client/lib/dictionary";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import PushChannels from "./PushChannels";

const tp = t.settings.push;

const formatWhen = (iso: string | null): string => {
  if (!iso) return tp.neverDelivered;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? tp.neverDelivered : d.toLocaleString("cs-CZ");
};

const deviceLabel = (endpoint: string): string => {
  try {
    const { host } = new URL(endpoint);
    return `${host} …${endpoint.slice(-8)}`;
  } catch {
    return `…${endpoint.slice(-12)}`;
  }
};

const PushTabPanel: React.FC = () => {
  const { permission, subscribed, supported, busy, iosNeedsInstall, enable, disable, sendTest } = useNotification();
  const { data, refetch } = useAsync(() => getWebPushDevices(), []);
  const [thisEndpoint, setThisEndpoint] = useState<string | null>(null);

  const devices = data?.responseObject ?? [];

  useEffect(() => {
    void getSubscription().then((s) => setThisEndpoint(s?.endpoint ?? null));
  }, [subscribed]);

  const handleEnable = useCallback(async () => {
    await enable();
    await refetch();
  }, [enable, refetch]);

  const handleDisable = useCallback(async () => {
    await disable();
    await refetch();
  }, [disable, refetch]);

  const handleRemove = useCallback(
    async (endpoint: string) => {
      if (endpoint === thisEndpoint) {
        await disable();
      } else {
        await postWebPushUnsubscribe(endpoint);
      }
      await refetch();
    },
    [thisEndpoint, disable, refetch]
  );

  const permissionBadge = () => {
    if (permission === "granted") return <Badge variant="default">{tp.permissionGranted}</Badge>;
    if (permission === "denied") return <Badge variant="destructive">{tp.permissionDenied}</Badge>;
    return <Badge variant="secondary">{tp.permissionDefault}</Badge>;
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">{tp.title}</h3>
          {supported && permissionBadge()}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{tp.description}</p>
      </div>

      {iosNeedsInstall && (
        <p className="text-xs rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 px-3 py-2">
          {tp.iosInstallHint}
        </p>
      )}

      {supported ? (
        <>
          <div className="flex flex-wrap gap-2">
            {subscribed ? (
              <Button variant="outline" size="sm" onClick={handleDisable} disabled={busy}>
                <BellOff className="h-4 w-4" />
                {tp.disable}
              </Button>
            ) : (
              <Button variant="default" size="sm" onClick={handleEnable} disabled={busy || permission === "denied"}>
                <Bell className="h-4 w-4" />
                {tp.enable}
              </Button>
            )}
            {subscribed && (
              <Button variant="secondary" size="sm" onClick={sendTest} disabled={busy}>
                <Send className="h-4 w-4" />
                {tp.test}
              </Button>
            )}
          </div>

          <div>
            <h4 className="text-xs font-medium text-muted-foreground">{tp.devices}</h4>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">{tp.devicesHint}</p>
            {devices.length === 0 ? (
              <p className="text-xs text-muted-foreground">{tp.noDevices}</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {devices.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-border px-2.5 py-2 text-xs"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 font-medium">
                        <Smartphone className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{d.userAgent || deviceLabel(d.endpoint)}</span>
                        {d.endpoint === thisEndpoint && (
                          <Badge variant="outline" className="shrink-0">
                            {tp.thisDevice}
                          </Badge>
                        )}
                      </div>
                      <div className="text-muted-foreground mt-0.5">
                        {tp.lastSuccess}: {formatWhen(d.lastSuccessAt)}
                        {d.failureCount > 0 && ` · ${tp.failures}: ${d.failureCount}`}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-destructive"
                      onClick={() => handleRemove(d.endpoint)}
                      disabled={busy}
                      aria-label={tp.removeDevice}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">{tp.unsupported}</p>
      )}

      <PushChannels />
    </div>
  );
};

export default React.memo(PushTabPanel);
